import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { callFunction } from '../lib/Helpers';
import { Speaker } from '../widgets/TTS/Toolbox/SpeakerModal';
import { TeamsFxContext } from '../Context';
import { SelectionRange } from '../widgets/TTS/EditablePage/EditableBlock';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';

export interface ProjectInfo {
  description: string;
  id: string;
  name: string;
}

export interface Block {
  id: string;
  html: string;
  controls: BlockControl;
  selections: EffectSelection[];
}

export interface PronunciationEffect {
  type: 'interpret' | 'alias';
  value: string;
}
export interface BreakEffect {
  type: 'time' | 'strength';
  value: string;
}

export interface BlockControl {
  speaker: Speaker;
  speed: string;
  pitch: string;
  volume: string;
  style: string | undefined;
  pronunciation?: PronunciationEffect | null;
  break?: BreakEffect | null;
}

interface ToolboxContext {
  block: HTMLElement | null;
  id: string;
  selection_id?: string;
  selection: SelectionRange | undefined;
  openModal: () => void;
}

interface ToolboxOptions {
  id: string;
  position: {
    top: number;
    left: number;
  };
  selectionId?: string;
  selection?: SelectionRange;
  openModalAction: () => void;
}

export interface EffectSelection {
  id: string;
  start: number;
  end: number;
  controls: {
    control: string;
    value: string | PronunciationEffect;
  }[];
}

export interface Selections {
  block_id: string;
  selections: EffectSelection[];
}

export interface CachedAudio {
  blockId: string;
  url: string;
  hasChanged: boolean;
}

interface TtsContextType {
  isToolboxVisible: boolean;
  hideToolbox: () => void;
  showToolbox: (context: ToolboxContext) => void;
  toolboxOptions: ToolboxOptions | undefined;

  speechToken: any;

  fetchSpeechToken: () => void;
  setAudioTrack: (url: string) => void;
  audioUrl: string;

  limitReached: boolean;
  setLimitReached: (limitReached: boolean) => void;
}

interface TtsContextProviderProps {
  children: ReactNode;
}

export const TtsContext = createContext({} as TtsContextType);

export function TtsContextProvider({ children }: TtsContextProviderProps) {
  const appInsights = useAppInsightsContext();

  const [limitReached, setLimitReached] = useState(false);

  const [isToolboxVisible, setIsToolboxVisible] = useState(false);
  const [toolboxOptions, setToolboxOptions] = useState<ToolboxOptions>();

  const [audioUrl, setAudioUrl] = useState<string>('');

  const [speechToken, setSpeechToken] = useState<any>();
  const teamsUserCredential = useContext(TeamsFxContext).teamsUserCredential;

  useEffect(() => {
    fetchSpeechToken();
  }, []);

  useEffect(() => {
    if (speechToken) {
      const NINE_MINUTES_TO_RENEW = 540000;
      const RENEWAL_TIMER = setTimeout(fetchSpeechToken, NINE_MINUTES_TO_RENEW);

      return () => clearTimeout(RENEWAL_TIMER);
    }
  }, [speechToken]);

  function fetchSpeechToken() {
    callFunction(
      true,
      'getSpeechToken?{0}',
      undefined,
      false,
      teamsUserCredential
    )
      .then((data) => {
        setSpeechToken(data);
      })
      .catch((error) => {
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackException({
            exception: error,
            properties: { component: 'TtsApp', location: 'onGetSpeechToken' },
          });
      });
  }

  function showToolbox(context: ToolboxContext): void {
    const offsetTop = context?.block?.getBoundingClientRect().top;
    const left = context?.block?.getBoundingClientRect().left;

    const selectionId = context?.selection_id;

    setToolboxOptions({
      id: context.id,
      position: {
        top: offsetTop ? offsetTop + 40 : 40,
        left: left ?? 0,
      },
      selectionId,
      selection: context.selection,
      openModalAction: context.openModal,
    });

    setIsToolboxVisible(true);
  }

  function hideToolbox() {
    setIsToolboxVisible(false);
  }

  function setAudioTrack(url: string) {
    if (url !== audioUrl) {
      setAudioUrl(url);
    }
  }

  return (
    <TtsContext.Provider
      value={{
        isToolboxVisible,
        toolboxOptions,
        hideToolbox,
        showToolbox,

        speechToken,
        fetchSpeechToken,

        setAudioTrack,
        audioUrl,

        limitReached,
        setLimitReached,
      }}
    >
      {children}
    </TtsContext.Provider>
  );
}
